import { useState } from "react";
import { Gift, CreditCard, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ADDONS, SERVICES } from "@/lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESET_AMOUNTS = [75, 130, 175, 250, 350, 500];

export default function GiftCards() {
  const [mode, setMode] = useState("package"); // 'package' or 'amount'
  const [packageId, setPackageId] = useState(SERVICES[0].id);
  const [amount, setAmount] = useState(130);
  const [custom, setCustom] = useState("");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [form, setForm] = useState({
    recipient_name: "",
    recipient_email: "",
    sender_name: "",
    sender_email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const selectedPackage = SERVICES.find((s) => s.id === packageId) || SERVICES[0];
  const packagePrice = selectedPackage.durations[0].price;
  const finalAmount = mode === "package" ? packagePrice : (custom ? Number(custom) : amount);
  const addonTotal = selectedAddons.reduce(
    (sum, id) => sum + (ADDONS.find((a) => a.id === id)?.price || 0), 0
  );
  const grandTotal = (finalAmount || 0) + addonTotal;

  const toggleAddon = (id) => {
    setSelectedAddons((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.recipient_name || !form.recipient_email || !form.sender_name) {
      toast.error("Please fill the recipient and your name.");
      return;
    }
    if (mode === "amount" && (!finalAmount || finalAmount < 25)) {
      toast.error("Minimum gift card amount is $25.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        ...form,
        addon_ids: selectedAddons,
        origin_url: window.location.origin,
      };
      if (mode === "package") {
        body.package_id = packageId;
      } else {
        body.amount = finalAmount;
      }
      const res = await axios.post(`${API}/payments/gift-card-checkout`, body);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Payment could not be initialized. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Payment could not be initialized.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-gift-cards" className="bg-spa-cream">
      <section className="container-spa pt-16 sm:pt-20 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="overline mb-5">Gift Cards</p>
            <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-spa-dark leading-[1.02]">
              The gift of<br />a quiet hour.
            </h1>
            <p className="mt-7 text-lg text-spa-gray leading-relaxed">
              Gift cards are delivered by email within minutes and can be redeemed for any service.
              No expiration, no nonsense.
            </p>
            <GiftPreviewCard
              total={grandTotal}
              selectedAddons={selectedAddons}
              packageName={mode === "package" ? selectedPackage.name : ""}
            />
          </div>

          <div className="lg:col-span-7">
            <div className="card-spa">
              <form onSubmit={submit} className="space-y-6">
                <ModeToggle mode={mode} onChange={setMode} />

                {mode === "package" ? (
                  <PackagePicker packageId={packageId} onChange={setPackageId} />
                ) : (
                  <AmountPicker
                    amount={amount}
                    custom={custom}
                    onPreset={(a) => { setAmount(a); setCustom(""); }}
                    onCustom={setCustom}
                  />
                )}

                <GiftAddonPicker selectedAddons={selectedAddons} onToggle={toggleAddon} />

                <div>
                  <p className="overline mb-4">Recipient</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input data-testid="gift-recipient-name" className="input-spa" placeholder="Recipient name" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} />
                    <input data-testid="gift-recipient-email" type="email" className="input-spa" placeholder="Recipient email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} />
                  </div>
                </div>

                <div>
                  <p className="overline mb-4">From you</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input data-testid="gift-sender-name" className="input-spa" placeholder="Your name" value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} />
                    <input data-testid="gift-sender-email" type="email" className="input-spa" placeholder="Your email (optional)" value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} />
                  </div>
                  <textarea data-testid="gift-message" className="input-spa min-h-[110px] mt-3 resize-none" placeholder="Add a personal note (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                </div>

                <div className="pt-4 border-t border-spa-border">
                  {addonTotal > 0 && (
                    <div className="flex items-center justify-between mb-2 text-sm text-spa-gray">
                      <span>Gift card ${finalAmount || 0} + add-ons ${addonTotal}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-5">
                    <span className="text-spa-gray">Total</span>
                    <span data-testid="gift-total" className="font-serif text-3xl text-spa-dark">${grandTotal || 0}</span>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="gift-checkout-button"
                    className="btn-primary w-full"
                  >
                    {submitting ? "Redirecting to Stripe..." : <><CreditCard className="w-4 h-4" strokeWidth={1.5} /> Pay with Stripe</>}
                  </button>
                  <p className="text-xs text-spa-gray text-center mt-3 flex items-center justify-center gap-1.5">
                    <Check className="w-3 h-3 text-spa-green" strokeWidth={1.5} /> Secure checkout · powered by Stripe
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const GiftPreviewCard = ({ total, selectedAddons, packageName }) => (
  <div className="mt-10 bg-spa-green text-spa-cream rounded-2xl p-8 relative overflow-hidden">
    <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-spa-gold/20 -translate-y-12 translate-x-12" />
    <Gift className="w-7 h-7 text-spa-gold mb-6" strokeWidth={1.5} />
    <p className="overline text-spa-gold mb-3">Mason Massage</p>
    <p className="font-serif text-5xl mb-1">${total || "—"}</p>
    <p className="text-sm text-spa-cream/70">
      {packageName ? `${packageName} · digital delivery` : "Gift card · digital delivery"}
    </p>
    {selectedAddons.length > 0 && (
      <p className="text-xs text-spa-gold mt-2">
        Includes {selectedAddons.map((id) => ADDONS.find((a) => a.id === id)?.name).join(" + ")}
      </p>
    )}
    <div className="mt-8 pt-6 border-t border-spa-cream/15 text-xs text-spa-cream/60">
      Redeemable for any in-home service · Knoxville, TN
    </div>
  </div>
);

const ModeToggle = ({ mode, onChange }) => (
  <div>
    <p className="overline mb-4">Choose a gift type</p>
    <div className="grid grid-cols-2 gap-3">
      <button
        type="button"
        data-testid="gift-mode-package"
        onClick={() => onChange("package")}
        className={`py-3 rounded-xl text-sm font-medium border transition-all ${
          mode === "package"
            ? "bg-spa-green text-spa-cream border-spa-green"
            : "bg-white border-spa-border text-spa-dark hover:border-spa-green"
        }`}
      >
        A massage package
      </button>
      <button
        type="button"
        data-testid="gift-mode-amount"
        onClick={() => onChange("amount")}
        className={`py-3 rounded-xl text-sm font-medium border transition-all ${
          mode === "amount"
            ? "bg-spa-green text-spa-cream border-spa-green"
            : "bg-white border-spa-border text-spa-dark hover:border-spa-green"
        }`}
      >
        A dollar amount
      </button>
    </div>
  </div>
);

const PackagePicker = ({ packageId, onChange }) => (
  <div>
    <p className="overline mb-4">Choose a package</p>
    <div className="space-y-3">
      {SERVICES.map((s) => {
        const selected = s.id === packageId;
        const d = s.durations[0];
        return (
          <button
            type="button"
            key={s.id}
            data-testid={`gift-package-${s.id}`}
            onClick={() => onChange(s.id)}
            aria-pressed={selected}
            className={`w-full flex items-center justify-between rounded-xl border px-4 py-4 text-left transition-all ${
              selected
                ? "bg-spa-green text-spa-cream border-spa-green"
                : "bg-white border-spa-border text-spa-dark hover:border-spa-green"
            }`}
          >
            <div>
              <p className={`font-serif text-xl ${selected ? "" : "text-spa-dark"}`}>{s.name}</p>
              <p className={`text-xs mt-0.5 ${selected ? "text-spa-cream/75" : "text-spa-gray"}`}>{d.label}</p>
            </div>
            <span className="font-serif text-2xl whitespace-nowrap">${d.price}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const AmountPicker = ({ amount, custom, onPreset, onCustom }) => (
  <div>
    <p className="overline mb-4">Choose an amount</p>
    <div className="grid grid-cols-3 gap-3">
      {PRESET_AMOUNTS.map((a) => (
        <button
          type="button"
          key={a}
          data-testid={`gift-amount-${a}`}
          onClick={() => onPreset(a)}
          className={`py-4 rounded-xl font-serif text-2xl border transition-all ${
            !custom && amount === a
              ? "bg-spa-green text-spa-cream border-spa-green"
              : "bg-white border-spa-border text-spa-dark hover:border-spa-green"
          }`}
        >
          ${a}
        </button>
      ))}
    </div>
    <input
      type="number"
      min="25"
      data-testid="gift-amount-custom"
      placeholder="Or enter a custom amount"
      value={custom}
      onChange={(e) => onCustom(e.target.value)}
      className="input-spa mt-3"
    />
  </div>
);

const GiftAddonPicker = ({ selectedAddons, onToggle }) => (
  <div>
    <p className="overline mb-4 flex items-center gap-2"><Plus className="w-3.5 h-3.5 text-spa-cyan" strokeWidth={1.5} /> Include add-ons (optional)</p>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {ADDONS.map((a) => {
        const checked = selectedAddons.includes(a.id);
        return (
          <button
            type="button"
            key={a.id}
            data-testid={`gift-addon-${a.id}`}
            onClick={() => onToggle(a.id)}
            aria-pressed={checked}
            className={`rounded-xl border px-4 py-3 text-left transition-all ${
              checked
                ? "bg-spa-green text-spa-cream border-spa-green"
                : "bg-white border-spa-border text-spa-dark hover:border-spa-green"
            }`}
          >
            <span className="block text-xs leading-snug">{a.name}</span>
            <span className="font-serif text-lg">+${a.price}</span>
          </button>
        );
      })}
    </div>
  </div>
);
